import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ethers } from 'ethers'
import { transferTokens, transferEther } from '../store/interactions'
import config from '../config.json'
import TOKEN_ARTIFACT from '../abis/Token.json'
import paxgIcon from '../assets/paxg.svg'
import usdtIcon from '../assets/usdt.svg'
import ethIcon from '../assets/eth.svg'
import mgIcon from '../assets/mg.svg'
import mgtvIcon from '../assets/mgtv.svg'

const TOKEN_ABI = TOKEN_ARTIFACT.abi

const tokenMeta = {
  PAXG: { icon: paxgIcon, name: 'Pax Gold' },
  USDT: { icon: usdtIcon, name: 'Tether' },
  MG:   { icon: mgIcon,   name: 'Milligram' },
  MGTV: { icon: mgtvIcon, name: 'Milligram TV' },
  ETH:  { icon: ethIcon,  name: 'Ethereum' }
}

const TokenRow = ({ symbol, walletBalance, exchangeBalance, token, canTransfer, isEther, provider, exchange, dispatch, mode }) => {
  const [amount, setAmount] = useState('')

  const meta = tokenMeta[symbol] || { icon: paxgIcon, name: symbol }
  const isDeposit = mode === 'deposit'
  const maxVal = isDeposit ? (walletBalance || '0') : (exchangeBalance || '0')

  const handleTransfer = () => {
    if (!amount) return
    if (isEther) {
      transferEther(provider, exchange, isDeposit ? 'Deposit' : 'Withdraw', amount, dispatch)
    } else {
      if (!token) return
      transferTokens(provider, exchange, isDeposit ? 'Deposit' : 'Withdraw', token, amount, dispatch)
    }
    setAmount('')
  }

  const handleMax = () => {
    setAmount(maxVal)
  }

  return (
    <div className="bal-row">
      <div className="bal-row__info">
        <img src={meta.icon} alt={symbol} className="bal-row__icon" />
        <span className="bal-row__symbol">{symbol}</span>
      </div>
      <div className="bal-row__col">
        <span className="bal-row__label">Wallet</span>
        <span className="bal-row__value">{walletBalance || '0'}</span>
      </div>
      <div className="bal-row__col">
        <span className="bal-row__label">Exchange</span>
        <span className="bal-row__value">{exchangeBalance || '0'}</span>
      </div>
      {canTransfer && (
        <div className="bal-row__actions">
          <div className="bal-row__input-wrap">
            <input
              type="text"
              placeholder="0.0000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bal-row__input"
            />
            <button
              type="button"
              className="bal-row__max"
              onClick={handleMax}
            >
              MAX
            </button>
          </div>
          <button
            className={`bal-row__action ${isDeposit ? 'bal-row__action--deposit' : 'bal-row__action--withdraw'}`}
            type="button"
            onClick={handleTransfer}
          >
            {isDeposit ? 'Deposit' : 'Withdraw'}
          </button>
        </div>
      )}
    </div>
  )
}

const BalancePage = ({ onClose }) => {
  const dispatch = useDispatch()
  const provider = useSelector(state => state.provider.connection)
  const account = useSelector(state => state.provider.account)
  const ethBalance = useSelector(state => state.provider.balance)
  const exchange = useSelector(state => state.exchange.contract)
  const chainId = useSelector(state => state.provider.chainId)
  const transferInProgress = useSelector(state => state.exchange.transferInProgress)

  const [balances, setBalances] = useState({})
  const [tokenContracts, setTokenContracts] = useState({})
  const [mode, setMode] = useState('deposit')

  useEffect(() => {
    const loadAllBalances = async () => {
      if (!provider || !account || !exchange?.address || !chainId || !config[chainId]) return

      const cfg = config[chainId]
      const symbols = ['PAXG', 'MG', 'MGTV', 'USDT']
      const contracts = {}
      const bals = {}

      for (const sym of symbols) {
        if (cfg[sym] && cfg[sym].address) {
          try {
            const contract = new ethers.Contract(cfg[sym].address, TOKEN_ABI, provider)
            contracts[sym] = contract

            const decimals = await contract.decimals()
            const walletBal = await contract.balanceOf(account)
            const exchBal = await exchange.balanceOf(cfg[sym].address, account)

            bals[sym] = {
              wallet: ethers.utils.formatUnits(walletBal, decimals),
              exchange: ethers.utils.formatUnits(exchBal, decimals)
            }
          } catch (e) {
            console.error(`Error loading ${sym} balance:`, e)
          }
        }
      }

      try {
        const ethExchBal = await exchange.balanceOf(ethers.constants.AddressZero, account)
        bals['ETH'] = {
          exchange: ethers.utils.formatEther(ethExchBal)
        }
      } catch (e) {
        console.error('Error loading ETH exchange balance:', e)
      }

      setTokenContracts(contracts)
      setBalances(bals)
    }

    loadAllBalances()
  }, [provider, account, exchange, chainId, transferInProgress, dispatch])

  const renderRow = (sym, { isEther } = {}) => (
    <TokenRow
      key={sym}
      symbol={sym}
      walletBalance={isEther ? (ethBalance ? Number(ethBalance).toFixed(4) : '0') : balances[sym]?.wallet}
      exchangeBalance={isEther ? (balances['ETH']?.exchange || '0') : (balances[sym]?.exchange)}
      token={tokenContracts[sym]}
      canTransfer={isEther || !!tokenContracts[sym]}
      isEther={!!isEther}
      provider={provider}
      exchange={exchange}
      dispatch={dispatch}
      mode={mode}
    />
  )

  return (
    <div className="balance-page">
      <div className="balance-page__header">
        <h2>Balances</h2>
        <div className="bal-toggle">
          <button
            className={`bal-toggle__btn ${mode === 'deposit' ? 'bal-toggle__btn--active' : ''}`}
            onClick={() => setMode('deposit')}
          >
            Deposit
          </button>
          <button
            className={`bal-toggle__btn ${mode === 'withdraw' ? 'bal-toggle__btn--active' : ''}`}
            onClick={() => setMode('withdraw')}
          >
            Withdraw
          </button>
        </div>
      </div>

      <div className="bal-groups">
        <div className="bal-group">
          <h3 className="bal-group__title">Gold</h3>
          {renderRow('PAXG')}
          {renderRow('MG')}
          {renderRow('MGTV')}
        </div>

        <div className="bal-group">
          <h3 className="bal-group__title">Currency</h3>
          {renderRow('ETH', { isEther: true })}
          {renderRow('USDT')}
        </div>
      </div>
    </div>
  )
}

export default BalancePage
